import { Boundaries } from "@planzone/core";
import { events } from "@planzone/events";
import { createStateReducer } from "../../utils/createStateReducer";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRequest(
  state: State,
  { resource }: Boundaries["retrieveResourceSubordinates"]["request"],
): State {
  const index = state.findIndex((req) => req.resource.uuid === resource.uuid);
  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          request: setRequestStateToPending(),
          resources: [],
          resource: resource,
          canLoadMore: false,
          currentPage: 0,
        },
      ];
}

function handleSuccess(
  state: State,
  {
    resource,
    team,
    size,
    currentPage,
  }: Boundaries["retrieveResourceSubordinates"]["success"],
): State {
  const index = state.findIndex((req) => req.resource.uuid === resource.uuid);
  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          request: setRequestStateToSuccess(),
          resources: [...state[index].resources, ...team],
          canLoadMore: team.length >= size,
          currentPage,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          request: setRequestStateToSuccess(),
          resources: team,
          resource: resource,
          canLoadMore: team.length >= size,
          currentPage,
        },
      ];
}

function handleFailure(
  state: State,
  { resource }: Boundaries["retrieveResourceSubordinates"]["failure"],
): State {
  const index = state.findIndex((req) => req.resource.uuid === resource.uuid);
  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          request: setRequestStateToFailure(),
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          request: setRequestStateToFailure(),
          resources: [],
          resource: resource,
          canLoadMore: false,
          currentPage: 0,
        },
      ];
}

function handleAssignManagerToResourceRequested(
  state: State,
  { resource, manager }: Boundaries["assignManagerToResource"]["request"],
): State {
  const index = state.findIndex((req) => req.resource.uuid === manager.uuid);
  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          request: setRequestStateToSuccess(),
          resources: [...state[index].resources, resource],
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          request: setRequestStateToSuccess(),
          resources: [resource],
          resource: manager,
          canLoadMore: false,
          currentPage: 0,
        },
      ];
}

function handleAssignManagerToResourceSuccess(
  state: State,
  { resource, manager }: Boundaries["assignManagerToResource"]["success"],
): State {
  const index = state.findIndex((req) => req.resource.uuid === manager.uuid);

  if (index === -1) {
    return [
      ...state,
      {
        request: setRequestStateToSuccess(),
        resources: [resource],
        resource: manager,
        canLoadMore: false,
        currentPage: 0,
      },
    ];
  }

  const resourceIndex = state[index].resources.findIndex(
    ({ uuid }) => uuid === resource.uuid,
  );

  return [
    ...state.slice(0, index),
    {
      ...state[index],
      request: setRequestStateToSuccess(),
      resources:
        resourceIndex !== -1
          ? [
              ...state[index].resources.slice(0, resourceIndex),
              resource,
              ...state[index].resources.slice(resourceIndex + 1),
            ]
          : [...state[index].resources, resource],
    },
    ...state.slice(index + 1),
  ];
}

function handleAssignManagerToResourceFailed(
  state: State,
  { resource, manager }: Boundaries["assignManagerToResource"]["failure"],
): State {
  const index = state.findIndex((req) => req.resource.uuid === manager.uuid);
  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          request: setRequestStateToSuccess(),
          resources: state[index].resources.filter(
            (r) => r.uuid !== resource.uuid,
          ),
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          request: setRequestStateToSuccess(),
          resources: [],
          resource: manager,
          canLoadMore: false,
          currentPage: 0,
        },
      ];
}

function handleUnAssignManagerFromResourceRequested(
  state: State,
  { resource, manager }: Boundaries["unAssignManagerFromResource"]["request"],
): State {
  const index = state.findIndex((req) => req.resource.uuid === manager.uuid);
  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          request: setRequestStateToSuccess(),
          resources: state[index].resources.filter(
            (r) => r.uuid !== resource.uuid,
          ),
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          request: setRequestStateToSuccess(),
          resources: [],
          resource: manager,
          canLoadMore: false,
          currentPage: 0,
        },
      ];
}

function handleUnAssignManagerFromResourceSuccess(
  state: State,
  { resource, manager }: Boundaries["unAssignManagerFromResource"]["success"],
): State {
  const index = state.findIndex((req) => req.resource.uuid === manager.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          request: setRequestStateToSuccess(),
          resources: state[index].resources.filter(
            (r) => r.uuid !== resource.uuid,
          ),
        },
        ...state.slice(index + 1),
      ]
    : state;
}

function handleUnAssignManagerFromResourceFailed(
  state: State,
  { resource, manager }: Boundaries["unAssignManagerFromResource"]["failure"],
): State {
  const index = state.findIndex((req) => req.resource.uuid === manager.uuid);
  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          request: setRequestStateToSuccess(),
          resources: [...state[index].resources, resource],
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          request: setRequestStateToSuccess(),
          resources: [resource],
          resource: manager,
          canLoadMore: false,
          currentPage: 0,
        },
      ];
}

function handleAssignEveryoneToManagerSuccess(
  state: State,
  { manager, subordinates }: Boundaries["assignEveryoneToManager"]["success"],
): State {
  const index = state.findIndex((req) => req.resource.uuid === manager.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          request: setRequestStateToSuccess(),
          resources: [
            ...state[index].resources,
            ...subordinates.filter(
              ({ uuid }) =>
                !state[index].resources.find((r) => r.uuid === uuid),
            ),
          ],
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          request: setRequestStateToSuccess(),
          resources: subordinates,
          resource: manager,
          canLoadMore: false,
          currentPage: 0,
        },
      ];
}

function handleSubordinatesRemoval(
  state: State,
  { manager, subordinateUuids }: Boundaries["removeSubordinates"]["success"],
): State {
  return state.map((s) =>
    s.resource.uuid === manager.uuid
      ? {
          ...s,
          resources: s.resources.filter(
            ({ uuid }) => !subordinateUuids.includes(uuid),
          ),
        }
      : s,
  );
}

function handleValidatePersonalTimeRecordsUpToDate(
  state: State,
  {
    resource,
    date,
  }: Boundaries["validatePersonalTimeRecordsUpToDate"]["success"],
): State {
  return state.map((s) =>
    s.resources.find((res) => res.uuid === resource.uuid)
      ? {
          ...s,
          resources: s.resources.map((pad) =>
            pad.uuid === resource.uuid ? { ...pad, integratedAt: date } : pad,
          ),
        }
      : s,
  );
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrieveResourceSubordinatesRequested]: handleRequest,
    [events.retrieveResourceSubordinatesSucceeded]: handleSuccess,
    [events.retrieveResourceSubordinatesFailed]: handleFailure,
    [events.assignManagerToResourceRequested]:
      handleAssignManagerToResourceRequested,
    [events.assignManagerToResourceSucceeded]:
      handleAssignManagerToResourceSuccess,
    [events.assignManagerToResourceFailed]: handleAssignManagerToResourceFailed,
    [events.unAssignManagerFromResourceRequested]:
      handleUnAssignManagerFromResourceRequested,
    [events.unAssignManagerFromResourceSucceeded]:
      handleUnAssignManagerFromResourceSuccess,
    [events.unAssignManagerFromResourceFailed]:
      handleUnAssignManagerFromResourceFailed,
    [events.assignEveryoneToManagerSucceeded]:
      handleAssignEveryoneToManagerSuccess,
    [events.removeSubordinatesSucceeded]: handleSubordinatesRemoval,
    [events.validatePersonalTimeRecordsUpToDateSucceeded]:
      handleValidatePersonalTimeRecordsUpToDate,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
